/**
 * AI CONTEXT
 * Purpose: prepares student table rows with tag/allergy icon enrichment, search, sort.
 * Uses: all students hook + Student mapper + settings-based search/sort config.
 * Used by: StudentsTable.
 * Importance: primary student list transformation layer.
 * Refactor: move JSX enrichment (TagIcons) to table renderers to keep data hook pure.
 */
import { orderBy } from "lodash"
import { useMemo } from "react"

import TagIcons from "#blocks/TagIcons"
import { Student } from "#classes/Student"
import { USER_SETTINGS } from "#constants/globalConstants"
import {
  STUDENT_SEARCHABLE_FIELDS,
  STUDENT_SORTABLE_FIELDS,
} from "#constants/studentOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import { StudentForTable } from "#customTypes/composedTypes/studentTypes"
import { SETTINGS_KEYS } from "#customTypes/globalTypes"
import { useGetAllStudents } from "#hooks/jotai/useGetStudents"
import useOptimizedSearch from "#hooks/newHooks/useOptimizedSearch"

import { useTableSort } from "./useTableSort"

const useStudentTableData = () => {
  const { getUserSetting } = useUserContext()
  const resultsCount = getUserSetting<number>(
    USER_SETTINGS.STUDENTS_TABLE_RESULTS_COUNT
  )
  const defaultSearchTerm = getUserSetting<string>(
    USER_SETTINGS.STUDENTS_TABLE_SEARCH_TERM
  )

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<StudentForTable>({
      tableKey: SETTINGS_KEYS.STUDENTS,
      sortableFields: STUDENT_SORTABLE_FIELDS,
    })

  const allStudents = useGetAllStudents()

  const isReady = allStudents?.length > 0

  const baseRows = useMemo(() => {
    // Wait for data OR timeout (5s) - allFulfilled becomes true after timeout
    if (!allStudents?.length || !isReady) return []

    return orderBy(allStudents, "lastUpdatedAt", "desc").map((student) => {
      const row = new Student(student).getStudentForTable()
      row.tags = <TagIcons values={student.tags} />
      row.allergies = <TagIcons values={student.allergies} />
      row.lastUpdatedAt = student.lastUpdatedAt
      return row
    })
  }, [allStudents, isReady])

  const sortedRows = useMemo(() => sortRows(baseRows), [sortRows, baseRows])

  const { filteredItems: filteredStudents, onSearch } =
    useOptimizedSearch<StudentForTable>(
      sortedRows,
      STUDENT_SEARCHABLE_FIELDS,
      resultsCount,
      defaultSearchTerm
    )

  const studentsForTable = useMemo(
    () => sortRows(filteredStudents),
    [sortRows, filteredStudents]
  )

  return {
    studentsForTable,
    onSearch,
    isReady,
    errors: null,
    resultsCount,
    sortField,
    sortOrder,
    onSortChange,
  }
}

export default useStudentTableData
